import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { buildDataTableLabels } from "@/components/table";
import { Link, Navigate, useParams, useSearchParams } from "react-router";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Calendar as CalendarIcon,
  RefreshCw,
  Store,
  Users,
} from "lucide-react";
import toast from "react-hot-toast";
import { useAuthStore } from "@/store/authStore";
import { bookingKeys, bookingService } from "@/services/bookingService";
import { activityService } from "@/services/activityService";
import { adminService } from "@/services/adminService";
import {
  pageSkip,
  serverPageWindow,
  useClampUrlPage,
  useUrlPage,
} from "@/hooks/useUrlPage";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import {
  Button,
  AlertModal,
  Pagination,
  EmptyState,
  buttonVariants,
} from "@/components/ui";
import {
  BookingsCalendar,
  BookingsDayPanel,
  BookingsDayGroupedList,
  BookingsViewToggle,
  BookingsListCardTitle,
  BookingsStatusFilter,
  BookingsActivityFilter,
  BookingsPartnerList,
  BookingsNeedsAttentionSection,
  type AttentionActionFilter,
  ReceiptConfirmModal,
  ReceiptOcrFailureModal,
  ReceiptManualAmountModal,
  BookingSellerNotesModal,
  mapBookingToListRow,
  groupBookingsByCalendarDate,
  buildOrderedDaySections,
  paginateDaySections,
  calendarDayTotalsFromRows,
  isCalendarDateKey,
  todayCalendarDateKey,
  addDaysToDateKey,
  weekStartFromDateKey,
  readBookingsViewPreference,
  writeBookingsViewPreference,
  parseBookingsViewMode,
  DEFAULT_ACTIVE_BOOKING_STATUSES,
  isBookingStatusFilterNarrowed,
  type BookingsViewMode,
  type CalendarDateKey,
  type CalendarDayTotals,
  type BookingListRow,
} from "@/components/bookings";
import type {
  Activity,
  Booking,
  BookingCardPage,
  BookingLifecycleStatus,
  BookingListFilters,
  BookingPageParams,
  ReceiptAnalyzeResponse,
} from "@/types";
import { Permission, UserRole } from "@/types";
import { PageFrame, LoadingState, ListResultsCard } from "@/components/common";
import { cn } from "@/lib/utils";
import { formatMoney } from "@/lib/money";
import { toastApiError, sanitizeUserFacingErrorText } from "@/lib/errorMessages";

const ITEMS_PER_PAGE_LIST = 10;
/** Tailwind `sm`: from this width the calendar view replaces the list. */
const CALENDAR_VISIBLE_QUERY = "(min-width: 40rem)";
/** A single calendar day rarely exceeds this; more rows page inside the day panel. */
const DAY_PANEL_PAGE_SIZE = 100;
const EMPTY_STATUS_COUNTS: Record<BookingLifecycleStatus, number> = {
  CREATED: 0,
  PENDING_HANDSHAKE: 0,
  ACCEPTED: 0,
  COMPLETED: 0,
  CANCELED: 0,
  REJECTED: 0,
  ABANDONED: 0,
};

interface ReceiptConfirmState {
  bookingId: string;
  bookingType: "service" | "receipt_based";
  storagePath: string;
  analyzeResult: ReceiptAnalyzeResponse;
}

interface ManualAmountState {
  bookingId: string;
  storagePath: string;
}

interface SellerNotesEditState {
  bookingId: string;
  initialNotes: string;
}

interface ReceiptOcrFailureState {
  booking: BookingListRow;
  storagePath: string;
  message: string;
  manualAmountRequired: boolean;
  remainingValidationAttempts?: number;
  maxValidationAttempts?: number;
}

export default function BookingsPage() {
  const { t: tCommon } = useTranslation("common");
  const tableLabels = buildDataTableLabels(tCommon);
  const { t } = useTranslation("bookings");
  const [searchParams, setSearchParams] = useSearchParams();
  const { sellerId: scopedSellerId } = useParams<{ sellerId?: string }>();
  const viewMode = parseBookingsViewMode(searchParams.get("view"));
  const itemsPerPage = ITEMS_PER_PAGE_LIST;
  const dateParam = searchParams.get("date");
  const selectedDateKey: CalendarDateKey | null =
    dateParam && isCalendarDateKey(dateParam) ? dateParam : null;
  const {
    getCurrentUserId,
    isCustomer,
    hasPermission,
    user,
  } = useAuthStore();
  const sellerUserId = getCurrentUserId();
  const customerMode = isCustomer();
  const adminMode = hasPermission(Permission.BOOKINGS_READ_ANY);
  const adminPartnerScope = adminMode && !!scopedSellerId;
  const sellerMode = user?.role === UserRole.SELLER;
  const hostMode = user?.role === UserRole.HOST;
  const activityOwnerMode = sellerMode || hostMode;
  const partnerMode = activityOwnerMode || adminPartnerScope;
  const queryClient = useQueryClient();
  const bookingsListPath = adminPartnerScope
    ? `/sellers/${scopedSellerId}/bookings`
    : "/bookings";

  const [currentPage, setCurrentPage] = useUrlPage();
  const calendarFitsScreen = useMediaQuery(CALENDAR_VISIBLE_QUERY);
  const calendarShown = viewMode === "calendar" && calendarFitsScreen;
  const [dayPanelPage, setDayPanelPage] = useState(1);
  const [selectedStatuses, setSelectedStatuses] = useState<
    BookingLifecycleStatus[]
  >([...DEFAULT_ACTIVE_BOOKING_STATUSES]);
  const [selectedActivityIds, setSelectedActivityIds] = useState<string[]>(
    [],
  );
  const [attentionFilter, setAttentionFilter] =
    useState<AttentionActionFilter>("all");
  const [processingByBookingId, setProcessingByBookingId] = useState<
    Record<string, boolean>
  >({});
  const [receiptConfirm, setReceiptConfirm] =
    useState<ReceiptConfirmState | null>(null);
  const [manualAmountEntry, setManualAmountEntry] =
    useState<ManualAmountState | null>(null);
  const [receiptOcrFailure, setReceiptOcrFailure] =
    useState<ReceiptOcrFailureState | null>(null);
  const [confirmingReceipt, setConfirmingReceipt] = useState(false);
  const [submittingManualAmount, setSubmittingManualAmount] = useState(false);
  const [receiptAlert, setReceiptAlert] = useState<{
    title: string;
    message: string;
    bookingId?: string;
  } | null>(null);
  const [fileInputResetKey, setFileInputResetKey] = useState<
    Record<string, number>
  >({});
  const [sellerNotesEdit, setSellerNotesEdit] =
    useState<SellerNotesEditState | null>(null);
  const [sellerNotesDraft, setSellerNotesDraft] = useState("");
  const [isSavingSellerNotes, setIsSavingSellerNotes] = useState(false);

  useEffect(() => {
    if (viewMode !== "calendar") return;
    if (selectedDateKey) return;

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("view", "calendar");
        next.set("date", todayCalendarDateKey());
        return next;
      },
      { replace: true },
    );
  }, [viewMode, selectedDateKey, setSearchParams]);

  const didRestoreViewPreference = useRef(false);
  useEffect(() => {
    if (didRestoreViewPreference.current) return;
    didRestoreViewPreference.current = true;

    if (searchParams.get("view") !== null || searchParams.get("date") !== null) {
      return;
    }

    const stored = readBookingsViewPreference();
    if (!stored || stored === "list") {
      return;
    }

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("view", "calendar");
        next.set("date", todayCalendarDateKey());
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  const { data: partner } = useQuery({
    queryKey: ["admin", "seller", scopedSellerId],
    queryFn: () => {
      if (!scopedSellerId) {
        throw new Error("scopedSellerId required");
      }
      return adminService.getSeller(scopedSellerId);
    },
    enabled: adminPartnerScope,
  });

  const activityOwnerUserId = adminPartnerScope
    ? scopedSellerId
    : sellerUserId;

  const { data: activities = [], isLoading: isLoadingActivities } = useQuery<Activity[]>({
    queryKey: ["activities"],
    queryFn: () => activityService.getAll(),
    enabled: !!activityOwnerUserId && !customerMode,
  });

  const { data: customerBookingsData, isFetching: isFetchingCustomerBookings } =
    useQuery<{
    bookings: Booking[];
  }>({
    queryKey: ["customerBookings"],
    queryFn: () => bookingService.getCustomerBookings(),
    enabled: customerMode,
  });

  const activityMap = useMemo(() => {
    const map = new Map<string, Activity>();
    activities.forEach((a) => map.set(a.id, a));
    return map;
  }, [activities]);

  const mappedCustomerRows = useMemo(
    () =>
      (customerBookingsData?.bookings ?? []).map((b) =>
        mapBookingToListRow(b, { activityMap, customerMode: true }),
      ),
    [customerBookingsData, activityMap],
  );

  const toPartnerRows = useCallback(
    (page: BookingCardPage | undefined): BookingListRow[] =>
      (page?.items ?? []).map((b) =>
        mapBookingToListRow(b, { activityMap, customerMode: false }),
      ),
    [activityMap],
  );

  // Admins list bookings only within a chosen seller scope; activity owners
  // are scoped to their own activities server-side.
  const partnerQueriesEnabled = adminMode ? adminPartnerScope : activityOwnerMode;
  const partnerScope = useMemo<BookingListFilters>(
    () => (adminPartnerScope ? { seller_user_id: scopedSellerId } : {}),
    [adminPartnerScope, scopedSellerId],
  );
  const partnerFilters = useMemo<BookingListFilters>(
    () => ({
      ...partnerScope,
      status: selectedStatuses.length > 0 ? selectedStatuses : undefined,
      activity_id:
        selectedActivityIds.length > 0 ? selectedActivityIds : undefined,
    }),
    [partnerScope, selectedStatuses, selectedActivityIds],
  );

  const listParams: BookingPageParams = {
    ...partnerFilters,
    order: "VISIT_ASC",
    skip: pageSkip(currentPage, ITEMS_PER_PAGE_LIST),
    limit: ITEMS_PER_PAGE_LIST,
  };
  const {
    data: partnerListPage,
    isLoading: isLoadingPartnerList,
    isFetching: isFetchingPartnerList,
    error: partnerListError,
    isPlaceholderData: isPartnerListPlaceholder,
  } = useQuery({
    queryKey: bookingKeys.page(listParams),
    queryFn: () => bookingService.getPage(listParams),
    // The list is hidden while the calendar is shown.
    enabled: partnerQueriesEnabled && !calendarShown,
    placeholderData: keepPreviousData,
  });

  const { data: partnerFacets } = useQuery({
    queryKey: bookingKeys.facets(partnerFilters),
    queryFn: () => bookingService.getFacets(partnerFilters),
    enabled: partnerQueriesEnabled,
    placeholderData: keepPreviousData,
  });

  // Calendar reads run only while the calendar is shown (calendar view, where
  // `?date=` is always set, on a wide enough screen); the today fallback just
  // keeps the params well-formed while disabled.
  const calendarQueriesEnabled = partnerQueriesEnabled && calendarShown;
  const calendarDateKey = selectedDateKey ?? todayCalendarDateKey();
  const calendarWeekStart = weekStartFromDateKey(calendarDateKey);
  const weekFilters: BookingListFilters = {
    ...partnerFilters,
    visit_date_from: calendarWeekStart,
    visit_date_to: addDaysToDateKey(calendarWeekStart, 6),
  };
  const { data: partnerWeekDays } = useQuery({
    queryKey: bookingKeys.days(weekFilters),
    queryFn: () => bookingService.getDays(weekFilters),
    enabled: calendarQueriesEnabled,
    placeholderData: keepPreviousData,
  });

  const dayPanelParams: BookingPageParams = {
    ...partnerFilters,
    visit_date_from: calendarDateKey,
    visit_date_to: calendarDateKey,
    order: "VISIT_ASC",
    skip: pageSkip(dayPanelPage, DAY_PANEL_PAGE_SIZE),
    limit: DAY_PANEL_PAGE_SIZE,
  };
  const { data: partnerDayPage } = useQuery({
    queryKey: bookingKeys.page(dayPanelParams),
    queryFn: () => bookingService.getPage(dayPanelParams),
    enabled: calendarQueriesEnabled,
    placeholderData: keepPreviousData,
  });

  const partnerListRows = useMemo(
    () => toPartnerRows(partnerListPage),
    [toPartnerRows, partnerListPage],
  );
  const partnerDayRows = useMemo(
    () => toPartnerRows(partnerDayPage),
    [toPartnerRows, partnerDayPage],
  );
  const partnerListTotal = partnerListPage?.total ?? 0;
  const partnerListTotalPages = serverPageWindow(
    currentPage,
    ITEMS_PER_PAGE_LIST,
    partnerListTotal,
  ).totalPages;
  const partnerDayPanelTotalPages = serverPageWindow(
    dayPanelPage,
    DAY_PANEL_PAGE_SIZE,
    partnerDayPage?.total ?? 0,
  ).totalPages;

  const partnerFiltersNarrowed =
    isBookingStatusFilterNarrowed(selectedStatuses) ||
    selectedActivityIds.length > 0;

  const partnerStatusCounts = partnerFacets?.by_status ?? EMPTY_STATUS_COUNTS;
  // Bookings matching every filter, in both views without fetching the list: each
  // status facet ignores only the status filter, so the selected statuses add up.
  const partnerMatchingTotal = partnerFacets
    ? (selectedStatuses.length > 0
        ? selectedStatuses.map((status) => partnerFacets.by_status[status])
        : Object.values(partnerFacets.by_status)
      ).reduce((sum, count) => sum + count, 0)
    : undefined;
  const partnerActivityBookingCounts = useMemo(
    () => new Map(Object.entries(partnerFacets?.by_activity ?? {})),
    [partnerFacets],
  );

  const partnerDayTotals = useMemo(
    () =>
      new Map<CalendarDateKey, CalendarDayTotals>(
        (partnerWeekDays ?? []).map((day) => [
          day.date,
          { bookings: day.bookings, guests: day.guests },
        ]),
      ),
    [partnerWeekDays],
  );

  const customerBookingRows = useMemo(() => {
    return [...mappedCustomerRows].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }, [mappedCustomerRows]);

  const customerBookingsByDate = useMemo(
    () => groupBookingsByCalendarDate(customerBookingRows),
    [customerBookingRows],
  );
  const customerDayTotals = useMemo(
    () => calendarDayTotalsFromRows(customerBookingRows),
    [customerBookingRows],
  );
  const customerSelectedDayBookings = selectedDateKey
    ? (customerBookingsByDate.get(selectedDateKey) ?? [])
    : [];

  const daySections = useMemo(
    () =>
      buildOrderedDaySections(customerBookingRows, {
        pastFirst: false,
      }),
    [customerBookingRows],
  );

  const { pageSections, totalPages } = useMemo(
    () => paginateDaySections(daySections, currentPage, itemsPerPage),
    [daySections, currentPage, itemsPerPage],
  );

  // Clamp a stale `?page=` (e.g. after filters shrink the list) once the total is known.
  useClampUrlPage(
    currentPage,
    partnerMode
      ? partnerListPage && !isPartnerListPlaceholder
        ? partnerListTotalPages
        : undefined
      : customerBookingsData
        ? totalPages
        : undefined,
  );

  useEffect(() => {
    setDayPanelPage(1);
  }, [selectedDateKey, partnerFilters]);

  const setViewMode = useCallback(
    (mode: BookingsViewMode) => {
      writeBookingsViewPreference(mode);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (mode === "calendar") {
            next.set("view", "calendar");
            next.set("date", todayCalendarDateKey());
          } else {
            next.delete("view");
            next.delete("date");
          }
          next.delete("page");
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const canInteractHandshake =
    adminMode
      ? hasPermission(Permission.HANDSHAKES_SESSION_CONFIRM_ANY) ||
        hasPermission(Permission.HANDSHAKES_SESSION_CANCEL)
      : hasPermission(Permission.HANDSHAKES_SESSION_CONFIRM) ||
        hasPermission(Permission.HANDSHAKES_MESSAGE_SEND);

  const partnerPageTitle =
    adminPartnerScope && partner?.name
      ? `${t("title")} — ${partner.name}`
      : t("title");

  const handleSelectCalendarDay = useCallback(
    (dateKey: CalendarDateKey) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("view", "calendar");
          next.set("date", dateKey);
          return next;
        },
        { replace: true },
      );
      requestAnimationFrame(() => {
        document
          .getElementById("bookings-day-panel")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    },
    [setSearchParams],
  );
  const isRefreshing = customerMode
    ? isFetchingCustomerBookings
    : isFetchingPartnerList;
  const isLoading =
    customerMode ? false : isLoadingPartnerList || (hostMode && isLoadingActivities);
  const error = partnerListError;

  const handleRefresh = () => {
    if (customerMode) {
      void queryClient.invalidateQueries({ queryKey: ["customerBookings"] });
    } else {
      void queryClient.invalidateQueries({ queryKey: bookingKeys.all });
    }
  };

  const handleReceiptUpload = async (
    booking: BookingListRow,
    file?: File | null,
  ) => {
    if (!file) return;
    const bookingId = booking.id;
    const bookingType = booking.type;

    setProcessingByBookingId((prev) => ({ ...prev, [bookingId]: true }));
    setReceiptOcrFailure(null);
    let uploadedPath: string | undefined;

    try {
      if (customerMode) {
        const captureResult = await bookingService.captureCustomerReceipt(
          bookingId,
          file,
        );
        if (!captureResult.ocr_success) {
          setReceiptAlert({
            title: t("receiptOcrUnreadableTitle"),
            message:
              captureResult.error_message || t("receiptOcrUnreadableFallback"),
            bookingId,
          });
          return;
        }
        setReceiptConfirm({
          bookingId,
          bookingType,
          storagePath: captureResult.storage_path,
          analyzeResult: captureResult,
        });
        return;
      }

      const uploadResult = await bookingService.uploadSellerReceipt(
        bookingId,
        file,
      );
      uploadedPath = uploadResult.path;

      const analyzeResult = await bookingService.analyzeSellerReceipt(
        bookingId,
        uploadResult.path,
      );

      if (!analyzeResult.ocr_success) {
        setReceiptOcrFailure({
          booking,
          storagePath: uploadResult.path,
          message:
            analyzeResult.error_message || t("receiptOcrUnreadableFallback"),
          manualAmountRequired: analyzeResult.manual_amount_required === true,
          remainingValidationAttempts:
            analyzeResult.remaining_validation_attempts,
          maxValidationAttempts: analyzeResult.max_validation_attempts,
        });
        return;
      }

      setReceiptConfirm({
        bookingId,
        bookingType,
        storagePath: uploadResult.path,
        analyzeResult,
      });
    } catch (e) {
      if (!customerMode) {
        setReceiptOcrFailure({
          booking,
          storagePath: uploadedPath ?? booking.latestUploadPath ?? "",
          message: t("receiptUploadErrorMessage"),
          manualAmountRequired: booking.manualAmountRequired === true,
        });
      } else {
        setReceiptAlert({
          title: t("receiptUploadErrorTitle"),
          message: t("receiptUploadErrorMessage"),
          bookingId,
        });
      }
      console.error(e);
    } finally {
      setProcessingByBookingId((prev) => ({ ...prev, [bookingId]: false }));
    }
  };

  const handleConfirmReceipt = async () => {
    if (!receiptConfirm) return;

    const { bookingId, storagePath, analyzeResult } = receiptConfirm;
    const totalCents = analyzeResult.extracted_total_cents ?? 0;

    setConfirmingReceipt(true);
    try {
      if (customerMode) {
        const result = await bookingService.confirmCustomerReceipt(
          bookingId,
          storagePath,
          totalCents,
        );
        toast.success(
          result.already_processed
            ? t("toastReceiptAlready")
            : t("toastPointsAwarded", { points: result.awarded_points }),
          { duration: 5000 },
        );
      } else {
        await bookingService.registerSellerReceipt(
          bookingId,
          storagePath,
          totalCents,
          "OCR",
        );
        toast.success(
          t("toastPartnerUploaded", {
            total: analyzeResult.extracted_total_display,
          }),
        );
      }
      void queryClient.invalidateQueries({ queryKey: ["customerBookings"] });
      void queryClient.invalidateQueries({ queryKey: ["bookings"] });
    } catch (e) {
      setReceiptAlert({
        title: t("receiptConfirmErrorTitle"),
        message: t("receiptConfirmErrorMessage"),
      });
      console.error(e);
    } finally {
      setConfirmingReceipt(false);
    }
    setReceiptConfirm(null);
  };

  const handleRejectReceipt = async () => {
    if (!receiptConfirm) return;

    if (customerMode) {
      setFileInputResetKey((prev) => ({
        ...prev,
        [receiptConfirm.bookingId]: (prev[receiptConfirm.bookingId] ?? 0) + 1,
      }));
      setReceiptConfirm(null);
      return;
    }

    const { bookingId, storagePath } = receiptConfirm;
    setConfirmingReceipt(true);
    try {
      const result = await bookingService.rejectSellerReceipt(
        bookingId,
        storagePath,
      );
      void queryClient.invalidateQueries({ queryKey: ["bookings"] });
      void queryClient.invalidateQueries({ queryKey: ["booking", bookingId] });
      if (result.manual_amount_required) {
        setManualAmountEntry({ bookingId, storagePath });
      } else {
        setFileInputResetKey((prev) => ({
          ...prev,
          [bookingId]: (prev[bookingId] ?? 0) + 1,
        }));
      }
    } catch (e) {
      setReceiptAlert({
        title: t("receiptConfirmErrorTitle"),
        message: t("receiptConfirmErrorMessage"),
      });
      console.error(e);
    } finally {
      setConfirmingReceipt(false);
      setReceiptConfirm(null);
    }
  };

  const handleManualAmountSubmit = async (totalCents: number) => {
    if (!manualAmountEntry) return;

    const { bookingId, storagePath } = manualAmountEntry;
    setSubmittingManualAmount(true);
    try {
      await bookingService.registerSellerReceipt(
        bookingId,
        storagePath,
        totalCents,
        "MANUAL",
      );
      toast.success(
        t("toastPartnerManualAmount", { total: formatMoney(totalCents) }),
      );
      void queryClient.invalidateQueries({ queryKey: ["bookings"] });
      void queryClient.invalidateQueries({ queryKey: ["booking", bookingId] });
      setManualAmountEntry(null);
    } catch (e) {
      setReceiptAlert({
        title: t("receiptConfirmErrorTitle"),
        message: t("receiptConfirmErrorMessage"),
      });
      console.error(e);
    } finally {
      setSubmittingManualAmount(false);
    }
  };

  const handleEditSellerNotes = useCallback(
    (bookingId: string, currentNotes: string | null) => {
      setSellerNotesDraft(currentNotes ?? "");
      setSellerNotesEdit({
        bookingId,
        initialNotes: currentNotes ?? "",
      });
    },
    [],
  );

  const handleSaveSellerNotes = async (bookingId: string, sellerNotes: string) => {
    setIsSavingSellerNotes(true);
    try {
      await bookingService.updateSellerNotes(bookingId, sellerNotes);
      toast.success(t("sellerNotesModal.saveSuccess"));
      setSellerNotesEdit(null);
      void queryClient.invalidateQueries({ queryKey: ["bookings"] });
      void queryClient.invalidateQueries({ queryKey: ["booking", bookingId] });
    } catch (e) {
      toastApiError(e, "generic", t("sellerNotesModal.saveError"));
      console.error(e);
    } finally {
      setIsSavingSellerNotes(false);
    }
  };

  if (adminMode && !scopedSellerId) {
    return (
      <PageFrame title={t("title")} icon={CalendarIcon}>
        <EmptyState
          variant="dashed"
          icon={Store}
          title={t("adminScopeTitle")}
          description={t("adminScopeRequired")}
          action={
            hasPermission(Permission.USERS_READ_ANY) ? (
              <Link
                to="/admin/users"
                className={buttonVariants({ variant: "outline", size: "sm" })}
              >
                <Users className="h-4 w-4" aria-hidden />
                {t("adminScopeAction")}
              </Link>
            ) : undefined
          }
        />
      </PageFrame>
    );
  }

  if (!sellerUserId && !customerMode && !adminPartnerScope) {
    return (
      <PageFrame
        title={t("title")}
        icon={CalendarIcon}
        description={t("descriptionSeller")}
        error
        errorMessage={t("sellerIdMissing")}
      />
    );
  }

  if (hostMode && !isLoadingActivities && activities.length === 0) {
    return <Navigate to="/" replace />;
  }

  if (error) {
    return (
      <PageFrame
        title={t("title")}
        icon={CalendarIcon}
        description={
          customerMode ? t("descriptionCustomer") : t("descriptionSeller")
        }
        error
        errorMessage={t("errorLoad")}
        onRetry={handleRefresh}
        isRetrying={isRefreshing}
      />
    );
  }

  const receiptModals = (
    <>
      {receiptConfirm ? (
        <ReceiptConfirmModal
          analyzeResult={receiptConfirm.analyzeResult}
          confirmingReceipt={confirmingReceipt}
          onConfirm={() => void handleConfirmReceipt()}
          onRetake={() => void handleRejectReceipt()}
        />
      ) : null}

      {receiptOcrFailure ? (
        <ReceiptOcrFailureModal
          message={receiptOcrFailure.message}
          manualAmountRequired={receiptOcrFailure.manualAmountRequired}
          remainingValidationAttempts={
            receiptOcrFailure.remainingValidationAttempts
          }
          maxValidationAttempts={receiptOcrFailure.maxValidationAttempts}
          isProcessing={
            processingByBookingId[receiptOcrFailure.booking.id] ?? false
          }
          onFileRetry={(file) =>
            void handleReceiptUpload(receiptOcrFailure.booking, file)
          }
          onManualAmount={() => {
            const path =
              receiptOcrFailure.storagePath ||
              receiptOcrFailure.booking.latestUploadPath;
            if (!path) return;
            setManualAmountEntry({
              bookingId: receiptOcrFailure.booking.id,
              storagePath: path,
            });
            setReceiptOcrFailure(null);
          }}
        />
      ) : null}

      {manualAmountEntry ? (
        <ReceiptManualAmountModal
          isSubmitting={submittingManualAmount}
          onSubmit={(totalCents) => void handleManualAmountSubmit(totalCents)}
          onClose={() => setManualAmountEntry(null)}
        />
      ) : null}

      {receiptAlert ? (
        <AlertModal
          closeLabel={tCommon("close")}
          isOpen={!!receiptAlert}
          onClose={() => {
            const bookingId = receiptAlert.bookingId;
            if (bookingId) {
              setFileInputResetKey((prev) => ({
                ...prev,
                [bookingId]: (prev[bookingId] ?? 0) + 1,
              }));
            }
            setReceiptAlert(null);
          }}
          title={receiptAlert.title}
          message={
            sanitizeUserFacingErrorText(
              receiptAlert.message,
              tCommon("somethingWentWrong"),
            ) ?? tCommon("somethingWentWrong")
          }
          variant="error"
          buttonText={t("alertGotIt")}
        />
      ) : null}

      <BookingSellerNotesModal
        isOpen={sellerNotesEdit != null}
        bookingId={sellerNotesEdit?.bookingId ?? null}
        notes={sellerNotesDraft}
        isSaving={isSavingSellerNotes}
        onNotesChange={setSellerNotesDraft}
        onClose={() => {
          if (!isSavingSellerNotes) {
            setSellerNotesEdit(null);
          }
        }}
        onSave={(bookingId, sellerNotes) =>
          void handleSaveSellerNotes(bookingId, sellerNotes)
        }
      />
    </>
  );

  if (partnerMode) {
    return (
      <PageFrame
        title={partnerPageTitle}
        icon={CalendarIcon}
        description={
          adminPartnerScope
            ? t("descriptionAdmin")
            : hostMode
              ? t("descriptionHost")
              : t("descriptionSeller")
        }
        backLink={
          adminPartnerScope
            ? {
                fallback: `/sellers/${scopedSellerId}`,
                children: t("backToPartner"),
              }
            : undefined
        }
      >
        {isLoading ? (
          <LoadingState message={t("loading")} />
        ) : (
          <div className="space-y-4 sm:space-y-6">
            <BookingsNeedsAttentionSection
              scope={partnerScope}
              activityMap={activityMap}
              filter={attentionFilter}
              onFilterChange={setAttentionFilter}
              bookingsListPath={bookingsListPath}
              adminMode={adminMode}
              canInteractHandshake={canInteractHandshake}
              processingByBookingId={processingByBookingId}
              fileInputResetKey={fileInputResetKey}
              onReceiptUpload={(booking, file) =>
                void handleReceiptUpload(booking, file)
              }
              onManualAmountClick={(bookingId, storagePath) =>
                setManualAmountEntry({ bookingId, storagePath })
              }
            />

            <ListResultsCard
              headerClassName="px-4 py-3 sm:px-5"
              contentClassName="px-4 py-4 sm:px-5"
              title={
                <BookingsListCardTitle
                  icon={CalendarIcon}
                  title={t("cardTitle")}
                  count={partnerMatchingTotal}
                />
              }
              headerAction={
                <BookingsViewToggle
                  viewMode={viewMode}
                  onViewModeChange={setViewMode}
                />
              }
              beforeContent={
                <div className="flex flex-col gap-2 border-b border-surface-200/70 bg-surface-50/60 px-4 py-3 sm:flex-row sm:items-start sm:px-5 dark:bg-surface-100/30">
                  <BookingsStatusFilter
                    className="min-w-0 sm:flex-1 [&>label]:sr-only"
                    value={selectedStatuses}
                    onChange={(next) => {
                      setSelectedStatuses(next);
                      setCurrentPage(1);
                    }}
                    statusCounts={partnerStatusCounts}
                  />
                  <BookingsActivityFilter
                    className="min-w-0 sm:flex-1 [&>label]:sr-only"
                    activities={activities}
                    bookingCountsByActivityId={partnerActivityBookingCounts}
                    selectedActivityIds={selectedActivityIds}
                    onSelectActivities={(activityIds) => {
                      setSelectedActivityIds(activityIds);
                      setCurrentPage(1);
                    }}
                  />
                </div>
              }
              isEmpty={
                calendarShown
                  ? partnerMatchingTotal === 0
                  : partnerListRows.length === 0
              }
              emptyState={
                <EmptyState
                  size="md"
                  icon={CalendarIcon}
                  title={
                    partnerFiltersNarrowed
                      ? t("emptyFilteredTitle")
                      : t("emptyTitle")
                  }
                  description={
                    partnerFiltersNarrowed
                      ? t("emptyFilteredDescription")
                      : t("emptyDescription")
                  }
                />
              }
              pagination={
                partnerListTotalPages > 1 ? (
                  <div className={cn(viewMode === "calendar" && "sm:hidden")}>
                    <Pagination
                      labels={tableLabels}
                      className="mt-5"
                      page={Math.min(currentPage, partnerListTotalPages)}
                      totalPages={partnerListTotalPages}
                      onPageChange={setCurrentPage}
                      isLoading={isFetchingPartnerList}
                      total={partnerListTotal}
                      showPageNumbers
                    />
                  </div>
                ) : null
              }
            >
              <>
                {viewMode === "calendar" ? (
                  selectedDateKey ? (
                    <div className="hidden space-y-3 sm:block sm:space-y-6">
                      <BookingsCalendar
                        selectedDateKey={selectedDateKey}
                        onSelectDate={handleSelectCalendarDay}
                        dayTotals={partnerDayTotals}
                      />
                      <BookingsDayPanel
                        dateKey={selectedDateKey}
                        bookings={partnerDayRows}
                        totals={
                          partnerDayTotals.get(selectedDateKey) ?? {
                            bookings: 0,
                            guests: 0,
                          }
                        }
                        pagination={
                          partnerDayPanelTotalPages > 1 ? (
                            <Pagination
                              labels={tableLabels}
                              className="mt-5"
                              page={dayPanelPage}
                              totalPages={partnerDayPanelTotalPages}
                              onPageChange={setDayPanelPage}
                              total={partnerDayPage?.total}
                              showPageNumbers
                            />
                          ) : null
                        }
                        bookingsListPath={bookingsListPath}
                        viewer="partner"
                        processingByBookingId={processingByBookingId}
                        fileInputResetKey={fileInputResetKey}
                        onReceiptUpload={(booking, file) =>
                          void handleReceiptUpload(booking, file)
                        }
                        onEditSellerNotes={handleEditSellerNotes}
                      />
                    </div>
                  ) : (
                    <div className="hidden sm:block">
                      <LoadingState message={t("loading")} />
                    </div>
                  )
                ) : null}

                <div className={cn(viewMode === "calendar" && "sm:hidden")}>
                  <BookingsPartnerList
                    rows={partnerListRows}
                    bookingsListPath={bookingsListPath}
                  />
                </div>
              </>
            </ListResultsCard>
          </div>
        )}

        {receiptModals}
      </PageFrame>
    );
  }

  const bookingsPageTitle =
    adminPartnerScope && partner?.name
      ? `${t("title")} — ${partner.name}`
      : t("title");
  const bookingsPageDescription = customerMode
    ? t("descriptionCustomer")
    : adminPartnerScope
      ? t("descriptionAdmin")
      : hostMode
        ? t("descriptionHost")
        : t("descriptionSeller");

  return (
    <PageFrame
      title={bookingsPageTitle}
      icon={CalendarIcon}
      description={bookingsPageDescription}
      backLink={
        adminPartnerScope
          ? {
              fallback: `/sellers/${scopedSellerId}`,
              children: t("backToPartner"),
            }
          : undefined
      }
      actions={
        <Button
          variant="outline"
          size="sm"
          isLoading={isRefreshing}
          leftIcon={<RefreshCw className="h-4 w-4" />}
          onClick={handleRefresh}
        >
          {tCommon("refresh")}
        </Button>
      }
    >
      <ListResultsCard
        headerClassName="px-4 py-3 sm:px-5"
        contentClassName="px-4 py-4 sm:px-5"
        title={
          <BookingsListCardTitle
            icon={CalendarIcon}
            title={t("cardTitle")}
            count={customerBookingRows.length}
          />
        }
        headerAction={
          <BookingsViewToggle
            viewMode={viewMode}
            onViewModeChange={setViewMode}
          />
        }
        isLoading={isLoading}
        loadingMessage={t("loading")}
        isEmpty={!isLoading && customerBookingRows.length === 0}
        emptyState={
          <EmptyState
            size="md"
            icon={CalendarIcon}
            title={t("emptyTitle")}
            description={
              customerMode
                ? t("emptyDescriptionCustomer")
                : t("emptyDescription")
            }
          />
        }
      >
        <>
          {viewMode === "calendar" ? (
            selectedDateKey ? (
              <div className="hidden space-y-3 sm:block sm:space-y-6">
                <BookingsCalendar
                  selectedDateKey={selectedDateKey}
                  onSelectDate={handleSelectCalendarDay}
                  dayTotals={customerDayTotals}
                />
                <BookingsDayPanel
                  dateKey={selectedDateKey}
                  bookings={customerSelectedDayBookings}
                  totals={
                    customerDayTotals.get(selectedDateKey) ?? {
                      bookings: 0,
                      guests: 0,
                    }
                  }
                  bookingsListPath={bookingsListPath}
                  viewer={customerMode ? "customer" : "staff"}
                  processingByBookingId={processingByBookingId}
                  fileInputResetKey={fileInputResetKey}
                  onReceiptUpload={(booking, file) =>
                    void handleReceiptUpload(booking, file)
                  }
                />
              </div>
            ) : (
              <div className="hidden sm:block">
                <LoadingState message={t("loading")} />
              </div>
            )
          ) : null}

          <div className={cn(viewMode === "calendar" && "sm:hidden")}>
            <BookingsDayGroupedList
              sections={pageSections}
              layout="row"
              bookingsListPath={bookingsListPath}
              viewer={customerMode ? "customer" : "staff"}
              processingByBookingId={processingByBookingId}
              fileInputResetKey={fileInputResetKey}
              onReceiptUpload={(booking, file) =>
                void handleReceiptUpload(booking, file)
              }
              onManualAmountClick={(bookingId, storagePath) =>
                setManualAmountEntry({ bookingId, storagePath })
              }
            />

            {totalPages > 1 ? (
              <Pagination
                labels={tableLabels}
                className="mt-5"
                page={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                showPageNumbers
              />
            ) : null}
          </div>
        </>
      </ListResultsCard>

      {receiptModals}
    </PageFrame>
  );
}
